<template>
  <v-card>
    <v-toolbar>
      <v-tabs v-model="tab">
        <v-tooltip v-for="t in tabs" :key="t.key" bottom>
          <template #activator="{ on, attrs }">
            <v-tab v-bind="attrs" v-on="on" class="payout-tabs">
              <v-badge
                v-if="tabData[t.key]?.total != null"
                :content="renderTotalCount(tabData[t.key].total)"
                :title="tabData[t.key].total"
              >
                {{ $i18n.translate(t.label) }}
              </v-badge>
              <span v-else>{{ $i18n.translate(t.label) }}</span>
            </v-tab>
          </template>
          <span>{{ t.infoMessage }}</span>
        </v-tooltip>
      </v-tabs>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          class="mr-6"
          v-model="search"
          :label="$i18n.translate('Filter') + ' ' + $i18n.translate('Payouts')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
      <v-menu offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" icon v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="effectiveItems"
                  v-model="filters.isEffective"
                  :label="$i18n.translate('Active') + ' / ' + $i18n.translate('Inactive')"
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="[
                    { text: 'Yes', value: true },
                    { text: 'No', value: false }
                  ]"
                  v-model="filters.communicatedToClient"
                  :label="$i18n.translate('Communicated to ') + $i18n.translate('Client') + '?'"
                  clearable
                  @input="onSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <DateTimePickerField
                  dense
                  v-model="filters.paidAfter"
                  :label="$i18n.translate('Paid After Date')"
                  defaultTime="00:00"
                  @input="onSubmit"
                ></DateTimePickerField>
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <DateTimePickerField
                  dense
                  v-model="filters.paidBefore"
                  :label="$i18n.translate('Paid Before Date')"
                  defaultTime="00:00"
                  @input="onSubmit"
                ></DateTimePickerField>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-btn v-if="$privilege.hasPrivilege('PAYOUT_CREATE')" fab small :to="{ name: 'payout', params: { id: 0 } }">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <div class="text-center" v-if="loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <v-tabs-items v-model="tab">
        <v-tab-item v-for="t in tabs" :key="t.key">
          <v-card>
            <v-card-text>
              <PayoutTable
                v-if="tabData[t.key]?.data"
                :items="tabData[t.key].data"
                :total="tabData[t.key].total"
                :loading="loading"
                :sale="sale"
                :participant="participant"
                :organization="organization"
                :nested="nested"
                :disabled="disabled"
                :tab="t.key"
                :options.sync="t.options"
                @refresh="getTabData(t.key)"
                @delete-success="getTabData('all')"
                @release-and-pay-success="getAllTabData()"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.payout-tabs {
  padding: 0px 30px !important;
}
</style>

<script>
import PayoutTable from "@/gapp-components/components/tables/PayoutTable.vue";
import { mapGetters } from "vuex";
import DateTimePickerField from "../../fields/DateTimePickerField.vue";

export default {
  components: { DateTimePickerField, PayoutTable },
  name: "Payouts",
  metaInfo: {
    title: "Payouts"
  },
  props: {
    sale: Object,
    participant: Object,
    organization: Object,
    nested: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      tab: 0,
      loading: false,
      headers: [],
      options: {
        itemsPerPage: 10,
        sortBy: ["updatedDate"],
        sortDesc: [true]
      },
      tabs: [],
      tabData: {},
      search: "",
      filters: {
        isEffective: null
      },
      effectiveItems: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
        { text: "All", value: null }
      ]
    };
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"])
  },
  created() {
    this.initializeTabs();
    this.getAllTabData();
  },
  methods: {
    initializeTabs() {
      const keys = ["all", "unpaid", "unissued", "issued", "expired"];
      const labels = ["All", "Unpaid", "Unissued", "Issued", "Expired"];
      const info = [
        "All payouts",
        "No payment or expiration recorded",
        "Payment pending; No expiration",
        "Successfully paid via bank transaction",
        "Expired transactions"
      ];

      this.tabs = keys.map((key, index) => {
        this.$set(this.filters, key, {
          isEffective: key === "expired" ? null : true,
          sale: undefined,
          participant: undefined,
          organization: undefined
        });
        return {
          key,
          label: labels[index],
          infoMessage: info[index],
          options: JSON.parse(JSON.stringify(this.options))
        };
      });
    },

    renderTotalCount(total) {
      if (total >= 1000000) return `${Math.floor(total / 1000000)}M+`;
      if (total >= 1000) return `${Math.floor(total / 1000)}K+`;

      return total.toLocaleString();
    },

    onSubmit() {
      this.getAllTabData();
    },
    onClear() {
      this.search = "";
      this.getAllTabData();
    },
    getAllTabData() {
      this.loading = true;
      const requests = this.tabs.map(tab => this.fetchTabData(tab.key));
      Promise.all(requests)
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    getTabData(tabKey) {
      this.loading = true;
      this.fetchTabData(tabKey)
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    fetchTabData(tabKey) {
      const tab = this.tabs.find(t => t.key === tabKey);
      const { sortBy, sortDesc, page = 1, itemsPerPage } = tab.options;
      const filter = {
        ...this.filters[tabKey],
        payoutStatus: tabKey,
        isEffective: tabKey === "expired" ? null : this.filters.isEffective,
        sale: this.sale?.id ? { id: this.sale.id } : undefined,
        participant: this.participant?.id ? { id: this.participant.id } : undefined,
        organization: this.organization?.id ? { id: this.organization.id } : undefined
      };

      if (this.search && this.search.length > 0) {
        filter.keyword = this.search;
      }

      if (this.filters.communicatedToClient !== null) {
        filter.communicatedToClient = this.filters.communicatedToClient;
      }

      if (this.filters.paidAfter !== null) filter.paidAfter = this.filters.paidAfter;
      if (this.filters.paidBefore !== null) filter.paidBefore = this.filters.paidBefore;
      return this.$api
        .post(
          `/api/payouts/search?expand=approval.approvalStage&expand=promotion&expand=participant&expand=organization&expand=sale&expand=saleProduct.product&expand=awardVehicle&size=${itemsPerPage}&page=${page -
            1}${sortBy?.length ? `&sort=${sortBy[0]},${sortDesc?.[0] ? "DESC" : "ASC"}` : ""}`,
          filter
        )
        .then(({ data }) => {
          this.$set(this.tabData, tabKey, {
            data: data?.content ?? [],
            total: data?.totalElements ?? 0
          });
        });
    }
  }
};
</script>
